import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

export default function LightLeakageRootCauseLabeling() {
  const { state } = useLocation();
  const {
    productInfo,
    imageUrl,
    rootCauses: prevRootCauses = [],
    recordingNotes: prevNotes = "",
    rectangles = [],
    yoloAnnotations = [],
    imageDimensions = { width: 0, height: 0 },
    canvasTransform = { scale: 1, offsetX: 0, offsetY: 0, scaledWidth: 0, scaledHeight: 0 },
  } = state || {};

  const [leakageType, setLeakageType] = useState("Dark State");
  const [rootCause, setRootCause] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [selectedRectangleIndices, setSelectedRectangleIndices] = useState([]);
  const navigate = useNavigate();

  const handleLeakageTypeSelection = (type) => {
    setLeakageType(type);
  };

  const handleRootCauseSelection = (cause) => {
    setRootCause(cause);
    if (cause !== "Others") {
      setAdditionalDetails("");
    }
  };

  const handleRectangleSelection = (index) => {
    setSelectedRectangleIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleSave = () => {
    let finalRootCause = rootCause;
    if (rootCause === "Others" && additionalDetails.trim()) {
      finalRootCause = additionalDetails.trim();
    }

    if (!finalRootCause) {
      alert("Please select a root cause or provide additional details for 'Others'.");
      return;
    }

    if (rectangles.length === 0 || yoloAnnotations.length === 0) {
      alert("No annotations provided. Please go back and annotate a defect.");
      return;
    }

    // If no rectangles are selected, associate all unassigned rectangles
    const assignedRectangleIds = prevRootCauses.flatMap((rc) => rc.rectangleIds || []);
    const availableRectangles = selectedRectangleIndices.length > 0
      ? selectedRectangleIndices
      : rectangles
          .map((_, index) => index)
          .filter((index) => !assignedRectangleIds.includes(index));

    if (availableRectangles.length === 0) {
      alert("Please select at least one rectangle or ensure there are unassigned rectangles.");
      return;
    }

    // Create a new root cause with associated rectangles
    const newRootCause = {
      id: Date.now().toString(), // Unique ID for the root cause
      leakageType,
      cause: finalRootCause,
      rectangleIds: availableRectangles, // Store indices of selected rectangles
    };

    const updatedRootCauses = [...prevRootCauses, newRootCause];

    const updatedNotes = updatedRootCauses
      .map((rc) => `${rc.leakageType}: ${rc.cause}`)
      .join("\n");

    navigate("/light-leakage-labeling", {
      state: {
        productInfo,
        imageUrl,
        rootCauses: updatedRootCauses,
        recordingNotes: updatedNotes,
        rectangles, // Pass all rectangles
        yoloAnnotations, // Pass YOLO annotations
        imageDimensions, // Pass image dimensions
        canvasTransform, // Pass canvas transform
      },
    });
  };

  const handleBack = () => {
    navigate("/light-leakage-inspection", {
      state: {
        productInfo,
        imageUrl,
        rootCauses: prevRootCauses,
        recordingNotes: prevNotes,
        rectangles,
        yoloAnnotations, // Pass YOLO annotations
        imageDimensions, // Pass image dimensions
        canvasTransform, // Pass canvas transform
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-white p-4">
      <div className="flex items-center mb-6">
        <button className="mr-4" onClick={handleBack}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-medium">LL Root Cause Labeling</h1>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto">
        {/* Light Leakage Type Selection */}
        <div className="my-6">
          <label className="block mb-3 text-center font-medium">Light Leakage Type:</label>
          <div className="flex space-x-4">
            {["Pressing", "Dark State"].map((type) => (
              <button
                key={type}
                className={`px-6 py-2 rounded-full ${
                  leakageType === type ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-400"
                }`}
                onClick={() => handleLeakageTypeSelection(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Root Cause Selection */}
        <div className="my-6">
          <label className="block mb-3 text-center font-medium">Root Cause</label>
          <div className="flex flex-wrap gap-2">
            {[
              "Front Cover Deformation",
              "Deco Deformation",
              "Middle Frame Deformation",
              "Back Cover Deformation",
              "Spacer Hardness",
              "OC Defect",
              "OC Gum Unevenness",
              "Others",
            ].map((cause) => (
              <button
                key={cause}
                className={`px-4 py-2 rounded-full text-sm transition ${
                  rootCause === cause
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-white hover:bg-gray-600"
                }`}
                onClick={() => handleRootCauseSelection(cause)}
              >
                {cause}
              </button>
            ))}
          </div>
        </div>

        {/* Additional Details for Others */}
        {rootCause === "Others" && (
          <div className="my-6">
            <label className="block mb-3 text-sm text-gray-400">Additional Details</label>
            <textarea
              className="w-full h-32 rounded-lg bg-gray-800 p-3 text-white placeholder-gray-400"
              placeholder="Enter additional details..."
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
            />
          </div>
        )}

       
      </div>

      <div className="pt-4">
        <button
          className="w-full bg-blue-600 text-white py-3 rounded-lg"
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );
}

